// src/tenantAdmin/pages/CompanyCreditsPage.jsx
//
// The "Crediti" tab: the COMPANY's wallet — the balance card plus how much each
// workspace spent. ⛔ Not the /credits page: that one leads with the per-user usage
// of the active workspace, a member's question; this is the company admin's.
// Rendered only under ActiveCompanyGuard, so the active workspace belongs to the
// console's company and reading on it IS reading the company's wallet (the wallet
// is one per company). No money column: showMoney is a superadmin right, and a
// superadmin never reaches this console. Pack purchases return to /credits.
//
// ⚠️ ASYMMETRIC FAILURE HANDLING, on purpose: the balance IS the page, so a failed
// read shows OUR sentence (never err.message); the per-workspace split is an extra,
// so any failure — a 403 on stale customer claims included — just hides the table.
import React, { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Wallet } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { fetchCreditBalance, fetchWalletSplit } from '../../services/api/creditsApi.js';
import { listCompanyWorkspaces } from '../companyApi.js';
import CreditBalanceCard from '../../components/credits/CreditBalanceCard.jsx';
import CreditUsageTable from '../../components/credits/CreditUsageTable.jsx';
import SectionCard, { PAGE_COLUMN, CARD_BODY } from '../../components/ui/SectionCard.jsx';
import { COLORS, RADII, SEMANTIC } from '../../constants/theme.js';

export default function CompanyCreditsPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('credits');
  const { activeTenant, tenants, isCustomerAdminOf } = useAuth();
  const companyId = useOutletContext()?.company?.companyId || null;
  const tenantId = activeTenant?.tenant_id || null;

  const [balance, setBalance] = useState(null);
  const [split, setSplit] = useState(null);
  const [names, setNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!tenantId) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true); setError(''); setSplit(null);
    fetchCreditBalance({ tenant: tenantId })
      .then((b) => { if (!cancelled) setBalance(b); })
      // ⛔ Our sentence, never err.message.
      .catch(() => { if (!cancelled) { setBalance(null); setError(t('manage.credits.loadFailed')); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    // The split is an extra: any failure (403 on stale claims included) just hides it.
    fetchWalletSplit({ tenant: tenantId })
      .then((s) => { if (!cancelled) setSplit(s); })
      .catch(() => { if (!cancelled) setSplit(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Names from the company's OWN list (covers retired and not-yet-claimed
  // workspaces, which the sign-in claims do not); ⛔ only for an administered id.
  useEffect(() => {
    if (!companyId || !isCustomerAdminOf?.(companyId)) return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => listCompanyWorkspaces(companyId))
      .then((d) => {
        if (cancelled) return;
        setNames(Object.fromEntries((d?.tenants || []).map((w) => [w.id, w.display_name])));
      })
      .catch(() => { /* fall back to the claims below */ });
    return () => { cancelled = true; };
  }, [companyId, isCustomerAdminOf]);

  const resolveTenantName = (id) => names[id] || (tenants || []).find((x) => x.tenant_id === id)?.display_name;

  return (
    <div style={PAGE_COLUMN}>
      <SectionCard
        icon={Wallet}
        title={t('manage.tabs.credits')}
        subtitle={t('manage.credits.subtitle')}
        action={(
          <Link to="/tenant-admin/manage/plans" style={{ fontSize: '13px', fontWeight: 600, color: COLORS.accent }}>
            {t('manage.credits.plansLink')}
          </Link>
        )}
      >
        <div style={{ ...CARD_BODY, display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {error && (
            <div role="alert" style={{
              padding: '10px 14px', borderRadius: RADII.control, fontSize: '13px',
              background: SEMANTIC.errore.bg, color: SEMANTIC.errore.fg,
            }}>{error}</div>
          )}
          <CreditBalanceCard balance={balance} showMoney={false} loading={loading} />
          {split && (
            <section aria-label={tc('usage.titleByTenant')}>
              <CreditUsageTable usage={split} showMoney={false} groupBy="tenant"
                resolveTenantName={resolveTenantName} activeTenantId={tenantId} />
              <p style={{ color: COLORS.textSecondary, fontSize: '12px', margin: '8px 2px 0' }}>{tc('split.note')}</p>
            </section>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
